import type { DragConstraint, DragUpdateResult } from "@project/emerge-types";
import type * as THREE from "three";
import { InteriorType } from "../../enum/InteriorType";
import type { FlightCaseSettings } from "../../interfaces/FlightCaseSettings";
import type { Rack19InteriorOptions } from "../../interfaces/Rack19InteriorOptions";
import { HETomm, mmToHE } from "../FlightCaseBlueprintCalculator";
import type {
  CaseCornerPosition,
  CornerDragConstraintResult,
  CornerDragHandler,
} from "./types";

const MIN_SIZE = 50;
const MAX_SIZE = 5000;

/**
 * Drag handler for RACK19 interior type.
 * - Bottom corners: only resize depth (width is fixed at 465mm)
 * - Top corners: resize height with HE snap anchors
 */
export const Rack19CornerDragHandler: CornerDragHandler = {
  shouldEnable(settings: FlightCaseSettings): boolean {
    return settings.interiorOptions.type === InteriorType.RACK19;
  },

  buildConstraint(
    caseCornerPosition: CaseCornerPosition,
    position: THREE.Vector3,
    innerDimensions: THREE.Vector3,
    settings: FlightCaseSettings,
  ): CornerDragConstraintResult | null {
    const isBottom = caseCornerPosition.endsWith("Bottom");
    const isFront = caseCornerPosition.includes("Front");

    const rack19 = settings.interiorOptions as Rack19InteriorOptions;
    const currentWidth = rack19.width ?? 465; // Fixed 19" width
    const currentHeight = rack19.height ?? 0;
    const currentDepth = rack19.depth ?? innerDimensions.z;

    const zSign = isFront ? -1 : 1;

    if (isBottom) {
      // Bottom corners: only resize depth (width is fixed at 465mm)
      const halfMinZ = MIN_SIZE / 2;
      const halfMaxZ = MAX_SIZE / 2;
      const minZ = zSign * halfMinZ;
      const maxZ = zSign * halfMaxZ;

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 0, 1], // Z axis only (depth)
        bounds: {
          min: [position.x, position.y, Math.min(minZ, maxZ)],
          max: [position.x, position.y, Math.max(minZ, maxZ)],
        },
        snapAnchors: [],
        sourceOptionUuid: "interior_corner_resize_z",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "z",
          interiorType: "rack19",
          zSign,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    } else {
      // Top corners: resize height with HE snap anchors
      const bottomY = -innerDimensions.y / 2;
      const minY = bottomY + MIN_SIZE;
      const maxY = bottomY + MAX_SIZE;

      // Generate snap points at each HE (rack unit) increment
      const snapAnchors: DragConstraint["snapAnchors"] = [];
      for (let he = 1; he <= 100; he++) {
        const heightMm = HETomm(he);
        const snapY = bottomY + heightMm;
        if (snapY >= minY && snapY <= maxY) {
          snapAnchors.push({
            position: [position.x, snapY, position.z],
            label: `${he} HE`,
          });
        }
      }

      const dragConstraint: DragConstraint = {
        type: "edge",
        axis: [0, 1, 0], // Vertical axis
        bounds: {
          min: [position.x, minY, position.z],
          max: [position.x, maxY, position.z],
        },
        snapAnchors,
        sourceOptionUuid: "interior_corner_resize_y",
      };

      return {
        dragConstraint,
        settingsContext: {
          caseCornerPosition,
          mode: "y",
          interiorType: "rack19",
          bottomY,
          currentWidth,
          currentHeight,
          currentDepth,
        },
      };
    }
  },

  updateSettings(
    newPosition: THREE.Vector3,
    settingsContext: Record<string, unknown>,
    currentSettings: FlightCaseSettings,
  ): DragUpdateResult<FlightCaseSettings> | null {
    const mode = settingsContext.mode as "z" | "y";
    const interior = currentSettings.interiorOptions as Rack19InteriorOptions;

    if (mode === "z") {
      // Bottom corner drag: only depth (width is fixed)
      const newDepth = Math.max(50, Math.round(Math.abs(newPosition.z) * 2));

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            depth: newDepth,
          },
        },
        displayValues: {
          primary: {
            label: "D",
            value: newDepth,
            unit: "mm",
          },
        },
      };
    } else {
      // Top corner drag: height with HE display
      const bottomY = settingsContext.bottomY as number;
      const newHeight = Math.max(50, Math.round(newPosition.y - bottomY));
      const heValue = mmToHE(newHeight);

      return {
        settingsUpdate: {
          interiorOptions: {
            ...interior,
            height: newHeight,
          },
        },
        displayValues: {
          primary: {
            label: "H",
            value: newHeight,
            unit: "mm",
          },
          secondary: {
            label: "",
            value: heValue,
            unit: "HE",
          },
        },
      };
    }
  },
};
